import React, { useState, useRef, useEffect } from "react";
import "./VideoUpload.scss";

import { useEvent } from "@hooks/useEvent";

import EVENTS from "../events";

import * as Material from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { Button } from "@fragments";

import { ReactComponent as Camera } from "@draws/camera.svg";
import Hls from "hls.js";

export default function VideoUpload({
    onLoaded,
    onSelected = () => { },
    url,
    handleRemoveVideo,
    aspectRatio,
}) {
    const [currentAspectRatio, setCurrentAspectRatio] = useState(aspectRatio);
    const [videoUrl, setVideoUrl] = useState(url);
    const videoInputRef = useRef(null);
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [loading, setLoading] = useState(false);

    useEvent(EVENTS.ASPECT_RATIO_CHANGE, (newVal) => {
        setCurrentAspectRatio(newVal);
    });

    // Synchronize local state with prop URL
    useEffect(() => {
        if (!loading) {
            setVideoUrl(url);
        }
    }, [url, loading]);

    // Handle video playback (Local, MP4, or HLS)
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl) return;

        // Cleanup previous HLS instance if it exists
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        if (videoUrl.includes(".m3u8")) {
            if (Hls.isSupported()) {
                const hls = new Hls();
                hls.loadSource(videoUrl);
                hls.attachMedia(video);
                hlsRef.current = hls;
            } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
                video.src = videoUrl;
            }
        } else {
            video.src = videoUrl;
        }

        // Cleanup Blob URLs to prevent memory leaks
        return () => {
            if (videoUrl && videoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(videoUrl);
            }
        };
    }, [videoUrl]);

    async function handleFileChange(event) {
        const file = event.target.files[0];
        const validTypes = ["video/mp4", "video/quicktime"];

        if (file && validTypes.includes(file.type)) {
            setLoading(true);

            const tempUrl = URL.createObjectURL(file);
            setVideoUrl(tempUrl);
            onSelected(tempUrl)

            window.addVideoForUpload(file, (videoUploaded) => {
                setLoading(false);
                setVideoUrl(videoUploaded.video.path);
                onLoaded(videoUploaded);
            });
        } else {
            alert("Atenção, apenas os formatos MP4 e MOV são permitidos");
        }
    }

    const isInVerticalMode = currentAspectRatio === 0.56;

    return (
        <div
            className={
                "video-upload " +
                (!videoUrl && "upload-mode ") +
                (isInVerticalMode ? " vertical-mode" : " horizontal-mode")
            }
            style={{
                aspectRatio: currentAspectRatio,
            }}
        >
            {loading && <Material.LinearProgress />}
            {videoUrl ? (
                <>
                    <Material.Tooltip title="Remover vídeo" arrow>
                        <Material.IconButton
                            className="remove-video"
                            children={<DeleteIcon color="white" />}
                            onClick={() => {
                                setVideoUrl(null);
                                handleRemoveVideo();
                            }}
                        />
                    </Material.Tooltip>
                    <div style={{ width: "100%", height: "100%" }}>
                        <video
                            ref={videoRef}
                            controls
                            autoPlay
                            muted
                            width="100%"
                            height="100%"
                            style={{ objectFit: "contain" }}
                        />
                    </div>
                </>
            ) : (
                <div className="upload-content" onClick={() => videoInputRef.current.click()}>
                    <Camera className="camera-video-upload" />
                    <h5 className="mt-2 bold">Adicione um vídeo da sua aula</h5>
                    <small style={{ opacity: 0.6 }}>(Opcional)</small>
                    <Button
                        variant="contained"
                        color="white"
                        endIcon={<AddIcon />}
                        text={"Adicionar vídeo"}
                        className="mt-1 mb-1"
                    />
                    <small style={{ opacity: 0.6 }}>
                        *Formatos permitidos: .mp4, .mov,{" "}
                        {!isInVerticalMode ? "16:9 (horizontal)" : "9/16 (vertical)"}
                    </small>
                    <input
                        type="file"
                        ref={videoInputRef}
                        style={{ display: "none" }}
                        accept="video/mp4,video/quicktime,.mp4,.mov"
                        onChange={handleFileChange}
                    />
                </div>
            )}
        </div>
    );
}
